import { useEffect, useState } from 'react';
import { myDohmFederation, type MyDohmFederationStatus } from '../infrastructure/my-dohm-federation';
import { useI18n } from '../i18n/I18nContext';
import { Button, Field, Icon } from './components';

export const AccountContactPanel = () => {
  const { t } = useI18n();
  const [status, setStatus] = useState<MyDohmFederationStatus>({ state: 'unavailable' });
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void myDohmFederation.status()
      .then((next) => { if (!cancelled) setStatus(next); })
      .catch(() => { if (!cancelled) setStatus({ state: 'unavailable' }); });
    return () => { cancelled = true; };
  }, []);

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      await action();
    } catch (error) {
      setMessage(t(
        error instanceof Error && error.message === 'MY_DOHM_CODE_INVALID'
          ? 'accountContact.codeInvalid'
          : error instanceof Error && (
            error.message === 'MY_DOHM_SESSION_REQUIRED' || error.message === 'MY_DOHM_NATIVE_SESSION_REQUIRED'
          )
            ? 'accountContact.sessionExpired'
            : 'accountContact.unavailable',
      ));
    } finally {
      setBusy(false);
    }
  };

  if (status.state === 'unavailable') {
    return <div className="sharing-card__stack">
      <p className="microcopy" role="status">{t('accountContact.unavailable')}</p>
      <Button disabled={busy} onClick={() => void run(async () => {
        setStatus(await myDohmFederation.status());
      })}>{t('accountContact.retry')}</Button>
      {message != null ? <p role="status">{message}</p> : null}
    </div>;
  }

  return (
    <div className="sharing-card__stack" data-testid="account-contact">
      <div className="notice notice--neutral">
        <Icon name="info" />
        <div>
          <p>{t('accountContact.optional')}</p>
          <small>{t('accountContact.privacy')}</small>
        </div>
      </div>

      {status.state === 'missing_contact' ? (
        <>
          <Field
            autoComplete="email"
            inputMode="email"
            label={t('accountContact.email')}
            hint={t('accountContact.emailHint')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button disabled={busy || !email.includes('@')} onClick={() => void run(async () => {
            const next = await myDohmFederation.configureContact(email);
            setEmail('');
            setStatus(next);
            setMessage(t(next.state === 'pending_contact_verification'
              ? 'accountContact.codeSent' : 'accountContact.requestQueued'));
          })}>{t('accountContact.configure')}</Button>
        </>
      ) : null}

      {status.state === 'contact_verification_available' ? (
        <Button disabled={busy} onClick={() => void run(async () => {
          const next = await myDohmFederation.requestContact();
          setStatus(next);
          setMessage(t(next.state === 'pending_contact_verification'
            ? 'accountContact.codeSent' : 'accountContact.requestQueued'));
        })}>{t('accountContact.resend')}</Button>
      ) : null}

      {status.state === 'pending_contact_verification' ? (
        <>
          <Field
            autoComplete="one-time-code"
            label={t('accountContact.code')}
            hint={t('accountContact.codeHint')}
            value={token}
            onChange={(event) => setToken(event.target.value)}
          />
          <Button disabled={busy || !/^\d{6}$/u.test(token.replace(/[\s-]/gu, ''))} onClick={() => void run(async () => {
            const recognition = await myDohmFederation.verifyContact(token.trim());
            setToken('');
            setStatus({ state: recognition === 'automatic' ? 'connected' : 'pending' });
            setMessage(t(recognition === 'automatic' ? 'accountContact.verified' : 'accountContact.queued'));
          })}>{t('accountContact.confirm')}</Button>
        </>
      ) : null}

      {status.state === 'connected' || status.state === 'contact_verified' ? (
        <div className="notice notice--success" role="status">
          <Icon name="check" />
          <p>{t('accountContact.verified')}</p>
        </div>
      ) : null}

      {status.state === 'pending' ? (
        <div className="notice notice--neutral" role="status">
          <Icon name="info" />
          <p>{t('accountContact.queued')}</p>
        </div>
      ) : null}

      {status.state === 'contact_verification_queued' ? (
        <div className="notice notice--neutral" role="status">
          <Icon name="info" />
          <p>{t('accountContact.requestQueued')}</p>
        </div>
      ) : null}

      {message != null ? <p className="import-message" role="status">{message}</p> : null}
    </div>
  );
};
